'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppSession } from '@/state/session/context'
import { useShouldAnimate } from '@/hooks/use-should-animate'
import { cn } from '@/lib/utils'
import { getDesktopNavigationGroups, isNavigationItemActive } from '@/lib/config/navigation'

const SIDEBAR_COLLAPSED_KEY = 'habitpack:desktop-sidebar:collapsed'

export function DesktopSidebar(): React.JSX.Element {
  const pathname = usePathname()
  const { session } = useAppSession()
  const groups = getDesktopNavigationGroups()
  const [collapsed, setCollapsed] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    setCollapsed(stored === '1')
  }, [])

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0')
      }
      return next
    })
  }

  const shouldAnimate = useShouldAnimate()

  // Prevent hydration mismatch by rendering a static version first
  if (!isMounted) {
    return <aside className="relative hidden w-64 shrink-0 lg:flex" />
  }

  const easing = 'cubic-bezier(0.25, 1, 0.5, 1)'
  const transitionStyle = shouldAnimate
    ? { transitionProperty: 'width, transform', transitionDuration: '300ms', transitionTimingFunction: easing }
    : undefined

  return (
    <aside
      style={{ width: collapsed ? 80 : 256, ...transitionStyle }}
      className="relative hidden shrink-0 lg:flex"
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        className="absolute top-6 -right-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground"
        aria-label={collapsed ? 'Expandir menu lateral' : 'Retrair menu lateral'}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronsRight className="size-3.5" aria-hidden="true" /> : <ChevronsLeft className="size-3.5" aria-hidden="true" />}
      </button>

      <div
        data-desktop-sidebar
        data-collapsed={collapsed ? 'true' : 'false'}
        className="flex h-full flex-col border-r bg-nav-bg py-5 overflow-x-hidden shrink-0"
        style={{
          borderColor: 'var(--border)',
          width: 256,
          paddingLeft: 16,
          paddingRight: 16,
          transform: collapsed ? 'translateX(-88px)' : 'translateX(0)',
          ...transitionStyle,
        }}
      >
        {groups.map((group, groupIndex) => (
          <section key={group.id} className="mb-4">
            {!collapsed && (
              <p
                className="mb-2 px-2 text-xs font-black tracking-[0.18em] text-muted-foreground uppercase whitespace-nowrap"
              >
                {group.label}
              </p>
            )}
            
            <nav className="flex flex-col gap-1" aria-label={group.label}>
              {group.items.map((item) => {
                const Icon = item.icon
                const isActive = isNavigationItemActive(pathname, item)
                const badgeValue = item.badgeKey ? session.navigationBadges[item.badgeKey] : 0

                const link = (
                  <Link
                    key={item.href}
                    href={item.href}
                    aria-label={item.label}
                    className={cn(
                      'flex min-h-[48px] items-center text-left text-sm font-bold transition-colors rounded-[calc(var(--radius)*0.75)]',
                      collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                      isActive
                        ? 'bg-tint-primary-subtle text-primary border border-primary/40'
                        : 'text-muted-foreground hover:text-foreground border border-transparent'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="relative inline-flex items-center shrink-0">
                      <Icon className="size-4" />
                      {badgeValue > 0 && collapsed && (
                        <span 
                          className="absolute -top-2 -right-2 rounded-full bg-tint-primary-subtle px-1.5 py-0.5 text-xs font-black text-primary"
                          aria-label={`${badgeValue > 9 ? 'Mais de 9' : badgeValue} notificações`}
                        >
                          {badgeValue}
                        </span>
                      )}
                    </span>
                    
                    {!collapsed && (
                      <span
                        className="truncate"
                      >
                        {item.label}
                      </span>
                    )}

                    {badgeValue > 0 && !collapsed && (
                      <span 
                        className="ml-auto rounded-full bg-tint-primary-subtle px-2 py-0.5 text-xs font-black text-primary shrink-0"
                        aria-label={`${badgeValue > 9 ? 'Mais de 9' : badgeValue} notificações`}
                      >
                        {badgeValue}
                      </span>
                    )}
                  </Link>
                )

                if (!collapsed) return link

                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </nav>

            {collapsed && groupIndex < groups.length - 1 ? (
              <div 
                className="mx-2 mt-3 h-px bg-border/70" 
              />
            ) : null}
          </section>
        ))}
      </div>
    </aside>
  )
}
